import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import * as RulesActions from '../actions/rules-actions';
import * as UiActions from '../actions/ui-actions';
import { Store } from '@ngrx/store';
import { AppState } from '../reducers';
import { OdataService } from '@app/Services/o-data.service';
import { AlertService, AlertType } from '@app/Services/Utilities/alert.service';
import { RuleData } from '@app/Shared/interfaces/iRules';

@Injectable()
export class RulesEffects {
    constructor(
        private actions$: Actions,
        private odataService: OdataService,
        private store: Store<AppState>,
        private alertService: AlertService
    ) {}

    /**
     * Map API field names to component field names
     * API: RuleKey, RuleTypeKey, RuleName, RuleFunction, RuleInitialParameter, RuleSubsequentParameter
     * Component: ruleKey, ruleTypeKey, ruleName, ruleFunction, ruleInitialParameter, ruleSubsequentParameter
     */
    private mapApiToComponent(apiRule: any): RuleData {
        return {
            DataFieldRuleKey: apiRule.DataFieldRuleKey || apiRule.DataFieldRuleKey,
            DataFieldRuleTypeKey: apiRule.DataFieldRuleTypeKey || apiRule.DataFieldRuleTypeKey,
            DataFieldRuleDefinition: apiRule.DataFieldRuleDefinition || apiRule.DataFieldRuleDefinition,
            DataFieldRuleNm: apiRule.DataFieldRuleNm || apiRule.DataFieldRuleNm,
            DataFieldRuleFirstVal: apiRule.DataFieldRuleFirstVal || apiRule.DataFieldRuleFirstVal || '',
            DataFieldRuleSecondVal: apiRule.DataFieldRuleSecondVal || apiRule.DataFieldRuleSecondVal || '',
        };
    }

    /**
     * Map component field names to API field names
     */
    private mapComponentToApi(componentRule: RuleData): any {
        const apiData: any = {
            DataFieldRuleDefinition: componentRule.DataFieldRuleDefinition,
            DataFieldRuleTypeKey: componentRule.DataFieldRuleTypeKey,
            DataFieldRuleNm: componentRule.DataFieldRuleNm,
            DataFieldRuleFirstVal: componentRule.DataFieldRuleFirstVal || '',
            DataFieldRuleSecondVal: componentRule.DataFieldRuleSecondVal || '',
        };

        // Only include RuleKey if it exists (for updates)
        if (componentRule.DataFieldRuleKey) {
            apiData.DataFieldRuleKey = componentRule.DataFieldRuleKey;
        }

        return apiData;
    }
loadRules$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RulesActions.loadRules),
            switchMap(() => {
                this.store.dispatch(UiActions.startLoading());
                return this.odataService.getRules().pipe(
                    map((rules: any[]) => {
                        const mappedRules = rules.map((rule: any) => this.mapApiToComponent(rule));
                        return RulesActions.loadRulesSuccess({ rules: mappedRules });
                    }),
                    tap(() => this.store.dispatch(UiActions.stopLoading())),
                    catchError((error) => {
                        this.store.dispatch(UiActions.stopLoading());
                        return of(RulesActions.loadRulesFailure({ error: error.message || 'Failed to load rules' }));
                    })
                );
            })
        )
    );

    createRule$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RulesActions.createRule),
            switchMap(({ ruleData }) => {
                this.store.dispatch(UiActions.startLoading());
                const apiData = this.mapComponentToApi(ruleData);
                return this.odataService.createRule(apiData).pipe(
                    map((response: any) => {
                        const createdRule = this.mapApiToComponent(response);
                        if (response.DataFieldRuleKey) {
                            createdRule.DataFieldRuleKey = response.DataFieldRuleKey;
                        }
                        return RulesActions.createRuleSuccess({ rule: createdRule });
                    }),
                    tap(() => this.store.dispatch(UiActions.stopLoading())),
                    catchError((error) => {
                        this.store.dispatch(UiActions.stopLoading());
                        return of(RulesActions.createRuleFailure({ error: error.message || 'Failed to create rule' }));
                    })
                );
            })
        )
    );
createRuleSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(RulesActions.createRuleSuccess),
                tap(() => {
                    this.alertService.notify(AlertType.Success, 'Rule created successfully', 'Success');
                })
            ),
        { dispatch: false }
    );

    createRuleFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(RulesActions.createRuleFailure),
                tap(({ error }) => {
                    this.alertService.notify(AlertType.Error, `Failed to create rule: ${error}`, 'Error');
                })
            ),
        { dispatch: false }
    );
updateRule$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RulesActions.updateRule),
            switchMap(({ key, ruleData }) => {
                this.store.dispatch(UiActions.startLoading());
                const apiData = this.mapComponentToApi(ruleData);
                return this.odataService.updateRule(key, apiData).pipe(
                    map((response: any) => {
                        const updatedRule = this.mapApiToComponent(response);
                        updatedRule.DataFieldRuleKey = key;
                        return RulesActions.updateRuleSuccess({ rule: updatedRule });
                    }),
                    tap(() => this.store.dispatch(UiActions.stopLoading())),
                    catchError((error) => {
                        this.store.dispatch(UiActions.stopLoading());
                        return of(RulesActions.updateRuleFailure({ error: error.message || 'Failed to update rule' }));
                    })
                );
            })
        )
    );

    updateRuleSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(RulesActions.updateRuleSuccess),
                tap(() => {
                    this.alertService.notify(AlertType.Success, 'Rule updated successfully', 'Success');
                })
            ),
        { dispatch: false }
    );

    updateRuleFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(RulesActions.updateRuleFailure),
                tap(({ error }) => {
                    this.alertService.notify(AlertType.Error, `Failed to update rule: ${error}`, 'Error');
                })
            ),
        { dispatch: false }
    );
deleteRule$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RulesActions.deleteRule),
            switchMap(({ key }) => {
                this.store.dispatch(UiActions.startLoading());
                // TODO: Implement deleteRule method in OdataService when DELETE endpoint is available
                this.store.dispatch(UiActions.stopLoading());
                return of(RulesActions.deleteRuleSuccess({ key }));
            })
        )
    );

    deleteRuleSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(RulesActions.deleteRuleSuccess),
                tap(() => {
                    this.alertService.notify(AlertType.Success, 'Rule deleted successfully', 'Success');
                })
            ),
        { dispatch: false }
    );

    deleteRuleFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(RulesActions.deleteRuleFailure),
                tap(({ error }) => {
                    this.alertService.notify(AlertType.Error, `Failed to delete rule: ${error}`, 'Error');
                })
            ),
        { dispatch: false }
    );
}
